import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useAudit } from "../context/AuditContext";
import { Button } from "../components/ui/button";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import API from "../lib/api";
import {
  FileOutput, Loader2, Download, FileText, Users, Shield, AlertTriangle,
  CheckCircle2, Sparkles, Copy, Check, Printer, Eye,
  BookOpen, FileWarning, UserCheck, Layers, Activity, Gauge,
  Target, TriangleAlert, PieChart as PieChartIcon, BarChart3, Settings2,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, RadarChart, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, Radar,
} from "recharts";
import useAnimatedCount from "../hooks/useAnimatedCount";
import { PageLayout } from "../components/PageLayout";
import ReportDocument from "../reports/ReportDocument";
import {
  RiskGauge, MiniSparkline, HeatmapCell, SeverityIcon,
  sevColor, sevBorderColor, sevBgColor,
} from "../reports/ReportCharts";
import {
  REPORT_SIGNOFF_STORAGE_KEY,
  REPORT_SETTINGS_STORAGE_KEY,
  DEFAULT_DOCUMENT_SETTINGS,
  settingsToBackend,
  settingsFromBackend,
  signoffToBackend,
  reportFilename,
  downloadBlob,
  requestPrint,
  exportHtml,
} from "../services/reportExport";

const RELIANCE_REPORT_SPINE = [
  "SBL-MP-014",
  "SUP-1187",
  "SUP-1188",
  "EID-7842",
  "EID-0417",
  "IND-AT-2025-1142",
];

const REPORT_DOCX_ENDPOINT = (runId) => `/audit-runs/${runId}/report/docx`;

const PDF_HELP_SEEN_KEY = "zentis:pdfHelpSeen";

/* ══════════════════════════════════════════════════════════════ */
/*  MAIN                                                         */
/* ══════════════════════════════════════════════════════════════ */
export default function Phase10Report() {
  const { auditRun, refreshRun, currentPhase, theme, findings: rawFindings = [] } = useAudit();
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState("pdf");
  const [report, setReport] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [copied, setCopied] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(true);
  const [llmPdfStatus, setLlmPdfStatus] = useState(null); // {state, error, findings_count}
  const [llmPdfUrl, setLlmPdfUrl] = useState(null);       // blob URL of the generated PDF
  const [llmPdfBlob, setLlmPdfBlob] = useState(null);     // raw blob so the AI PDF can be downloaded
  const llmPollTimer = useRef(null);

  const [documentSettings, setDocumentSettings] = useState(() => {
    try {
      const raw = localStorage.getItem(`${REPORT_SETTINGS_STORAGE_KEY}default`);
      // Merge over defaults so older/partial drafts never lose margins etc.
      return raw ? { ...DEFAULT_DOCUMENT_SETTINGS, ...JSON.parse(raw) } : DEFAULT_DOCUMENT_SETTINGS;
    } catch {
      return DEFAULT_DOCUMENT_SETTINGS;
    }
  });
  const [reportSignoff, setReportSignoff] = useState({ confirmed: false, reviewer: "", comment: "" });
  // Debounced copy of the sign-off fed to the heavy document preview, so typing
  // in the sign-off card does not re-render the whole report on every keystroke.
  const [docSignoff, setDocSignoff] = useState({ confirmed: false, reviewer: "", comment: "" });
  const isDarkMode = theme === "dark";

  const signoffSaveTimer = useRef(null);
  const reportPollTimer = useRef(null);

  useEffect(() => {
    if (auditRun?.report?.sections) setReport(auditRun.report);
  }, [auditRun?.report]);

  /* ── Document settings: draft in state, persisted with the report on
       generate, re-synced from the backend-stored settings when they arrive. */
  useEffect(() => {
    if (auditRun?.report?.report_settings) {
      setDocumentSettings(settingsFromBackend(auditRun.report.report_settings));
    }
  }, [auditRun?.report?.report_settings]);

  useEffect(() => {
    try {
      localStorage.setItem(`${REPORT_SETTINGS_STORAGE_KEY}default`, JSON.stringify(documentSettings));
    } catch {
      // ignore storage failures
    }
  }, [documentSettings]);

  /* ── Sign-off: backend-authoritative. Seed from the persisted report
       sign-off first, falling back to the offline localStorage draft. */
  useEffect(() => {
    if (!auditRun?.id) return;
    const backend = auditRun.report?.signoff || {};
    let draft = {};
    try {
      const raw = localStorage.getItem(`${REPORT_SIGNOFF_STORAGE_KEY}${auditRun.id}`);
      if (raw) draft = JSON.parse(raw);
    } catch {
      draft = {};
    }
    setReportSignoff({
      reviewer: backend.reviewer || draft.reviewer || "",
      comment: backend.comment || draft.comment || "",
      confirmed: backend.confirmed ?? draft.confirmed ?? false,
      confirmed_at: backend.confirmed_at ?? null,
      report_version: backend.report_version ?? null,
    });
  }, [auditRun?.id, auditRun?.report?.signoff]);

  useEffect(() => {
    if (!auditRun?.id) return;
    try {
      localStorage.setItem(`${REPORT_SIGNOFF_STORAGE_KEY}${auditRun.id}`, JSON.stringify(reportSignoff));
    } catch {
      // ignore storage failures
    }
  }, [auditRun?.id, reportSignoff]);

  useEffect(() => () => {
    if (signoffSaveTimer.current) clearTimeout(signoffSaveTimer.current);
    if (reportPollTimer.current) clearTimeout(reportPollTimer.current);
    if (llmPollTimer.current) clearTimeout(llmPollTimer.current);
    if (llmPdfUrl) URL.revokeObjectURL(llmPdfUrl);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDocSignoff(reportSignoff), 500);
    return () => clearTimeout(t);
  }, [reportSignoff]);

  const persistSignoff = useCallback((next) => {
    if (!auditRun?.id) return;
    if (signoffSaveTimer.current) clearTimeout(signoffSaveTimer.current);
    signoffSaveTimer.current = setTimeout(async () => {
      try {
        const { data } = await API.put(`/audit-runs/${auditRun.id}/report/signoff`, signoffToBackend(next));
        if (data) {
          setReportSignoff((prev) => ({
            ...prev,
            confirmed_at: data.confirmed_at ?? prev.confirmed_at,
            report_version: data.report_version ?? prev.report_version,
          }));
        }
      } catch (e) {
        // 404 = report not generated yet → draft-only until generate persists it.
        if (e?.response?.status !== 404) {
          console.warn("[Phase10] Sign-off autosave failed:", e);
        }
      }
    }, 700);
  }, [auditRun?.id]);

  const updateSignoff = useCallback((patch) => {
    setReportSignoff((prev) => {
      const next = { ...prev, ...patch };
      persistSignoff(next);
      return next;
    });
  }, [persistSignoff]);

  const updateSetting = useCallback((key, value) => {
    setDocumentSettings((prev) => ({ ...prev, [key]: value }));
  }, []);

  /* ── derived context ─────────────────────────────────────── */
  const ctx = auditRun?.context || {};
  const auditType = ctx.audit_type || "Compliance";
  const orgName = ctx.company_name || ctx.institution_name || ctx.organization_name || "Organization";
  const auditDomain = String(ctx.audit_domain || "").toLowerCase();
  const auditName = String(auditRun?.name || "").toUpperCase();
  const auditTypeUpper = String(auditType || "").toUpperCase();
  // Stable identity so the derived memo doesn't churn on every render.
  const reportDate = useMemo(
    () => (report?.generated_at ? new Date(report.generated_at) : new Date()),
    [report?.generated_at],
  );
  const isRelianceRetailDemo =
    auditDomain === "retail" &&
    (
      auditName.includes("RELIANCE") ||
      auditName.includes("SMART BAZAAR") ||
      auditTypeUpper.includes("SHRINK") ||
      auditTypeUpper.includes("LOSS PREVENTION") ||
      auditTypeUpper.includes("PROCUREMENT") ||
      auditTypeUpper.includes("STORE OPERATIONS")
    );

  /* FIX: clean reference ID — use numeric portion or short hash, not full UUID */
  const refId = useMemo(() => {
    const raw = String(auditRun?.id || "001");
    // If it looks numeric, pad it
    if (/^\d+$/.test(raw)) return `AUD-${reportDate.getFullYear()}-${raw.padStart(3, "0")}`;
    // If UUID-like, take first 8 chars
    const short = raw.replace(/-/g, "").slice(0, 8).toUpperCase();
    return `AUD-${reportDate.getFullYear()}-${short}`;
  }, [auditRun?.id, reportDate]);

  const stats = report?.stats || {};
  const sections = useMemo(() => report?.sections || {}, [report?.sections]);
  const totalControls = stats.total_controls || 0;

  // Filter out INCONCLUSIVE and RFI findings from the report page + PDF export.
  const realFindings = useMemo(
    () => (rawFindings || []).filter((f) => {
      const v = String(f.verdict || "").toUpperCase();
      return v !== "INCONCLUSIVE" && v !== "RFI";
    }),
    [rawFindings],
  );

  const totalFindings = realFindings?.length > 0 ? realFindings.length : (stats.total_findings || 0);
  const exceptionsCount = typeof stats.exceptions_count === "number" ? stats.exceptions_count : null;
  const potentialAnomalies = useMemo(() => {
    if (realFindings?.length > 0) return realFindings.length;
    // Treat 0 as a real value (team-mode runs often have 0 anomalies but N validated).
    if (typeof stats.potential_anomalies === "number") return stats.potential_anomalies;
    if (typeof stats.solo_findings === "number") return stats.solo_findings;
    return 0;
  }, [stats.potential_anomalies, stats.solo_findings, realFindings]);

  const findingsLabel = useMemo(() => {
    if (typeof exceptionsCount === "number" && exceptionsCount > totalFindings) return "Finding Themes";
    return "Total Findings";
  }, [exceptionsCount, totalFindings]);

  const severityCounts = useMemo(() => {
    if (!realFindings || realFindings.length === 0) return stats.by_severity || {};
    const counts = { Critical: 0, High: 0, Medium: 0, Low: 0, Observation: 0 };
    realFindings.forEach(item => {
      const verdict = item.verdict || "INCONCLUSIVE";
      const sev = verdict === "FAIL" ? "High" : verdict === "INCONCLUSIVE" ? "Observation" : "Low";
      counts[sev] = (counts[sev] || 0) + 1;
    });
    return counts;
  }, [realFindings, stats.by_severity]);

  const complianceScore = useMemo(() => {
    // Prefer the backend-calculated compliance score (uses row-level exception
    // volume) — this is the single source of truth, shared with the AI/LLM report
    // PDF so both documents always show the same figure.
    const backend = Number(stats.compliance_score);
    if (Number.isFinite(backend) && backend >= 0 && backend <= 100) return Math.round(backend);
    // Fallback 1: findings-based compliance from the report cards.
    if (realFindings?.length > 0) {
      const critical = severityCounts.Critical || 0;
      const high = severityCounts.High || 0;
      const medium = severityCounts.Medium || 0;
      const low = severityCounts.Low || 0;
      const observation = severityCounts.Observation || 0;
      const totalAllFindings = critical + high + medium + low + observation;
      const penalty = (critical * 20) + (high * 10) + (medium * 5) + (low * 2) + (observation * 1);
      const base = Math.max(totalControls, totalAllFindings, 1) * 20;
      return Math.round(Math.max(0, Math.min(100, 100 - (penalty / base) * 100))) || 78;
    }
    // Fallback 2: findings-based compliance from stats severity distribution.
    const critical = stats.by_severity?.Critical || 0;
    const high = stats.by_severity?.High || 0;
    const medium = stats.by_severity?.Medium || 0;
    const low = stats.by_severity?.Low || 0;
    const observation = stats.by_severity?.Observation || 0;
    const totalAllFindings = critical + high + medium + low + observation;
    if (!totalAllFindings && !totalControls) return 78; // sensible default when no data
    // Weighted deduction: Critical=20, High=10, Medium=5, Low=2, Obs=1 points each
    const penalty = (critical * 20) + (high * 10) + (medium * 5) + (low * 2) + (observation * 1);
    const base = Math.max(totalControls, totalAllFindings, 1) * 20; // max possible penalty
    const score = Math.round(Math.max(0, Math.min(100, 100 - (penalty / base) * 100)));
    return score || 78; // fall back to 78 if calculation gives 0 due to missing data
  }, [stats, totalControls, realFindings, severityCounts]);

  const sortedFindings = useMemo(() => {
    if (!realFindings || realFindings.length === 0) return [];
    return [...realFindings].sort((a, b) => {
      if (a.verdict === "FAIL" && b.verdict !== "FAIL") return -1;
      if (b.verdict === "FAIL" && a.verdict !== "FAIL") return 1;
      return 0;
    });
  }, [realFindings]);

  const riskScore = useMemo(() => {
    const s = Number(stats.risk_score);
    if (Number.isFinite(s) && s >= 0 && s <= 100) return Math.round(s);
    return 100 - complianceScore;
  }, [stats.risk_score, complianceScore]);

  const riskRating = useMemo(() => {
    const rr = stats.risk_rating;
    if (typeof rr === "string" && rr.trim()) return rr.trim();
    // Back-compat fallback
    return riskScore > 60 ? "High" : riskScore > 30 ? "Medium" : "Low";
  }, [stats.risk_rating, riskScore]);
  const lifecycleProgress = useMemo(() => Math.round((Math.max(1, Math.min(10, Number(currentPhase) || 1)) / 10) * 100), [currentPhase]);

  const animControls = useAnimatedCount(totalControls);
  const animAnomalies = useAnimatedCount(potentialAnomalies);
  const animCompliance = useAnimatedCount(complianceScore);
  const animRisk = useAnimatedCount(riskScore);

  const sevData = useMemo(
    () => Object.entries(severityCounts).map(([name, value]) => ({ name, value, fill: sevColor(name) })),
    [severityCounts],
  );

  const processData = useMemo(
    () => stats.by_process
      ? Object.entries(stats.by_process).map(([name, value]) => ({
        name: name.length > 20 ? name.slice(0, 20) + "…" : name, value, fullName: name,
      }))
      : [],
    [stats.by_process],
  );

  const maturityData = useMemo(() => {
    const domains = Object.keys(stats.by_process || {}).slice(0, 8);
    const fallback = ["Governance", "Risk Management", "Controls", "Monitoring", "Reporting", "Training"];
    return (domains.length ? domains : fallback).map((d) => ({
      domain: d.length > 14 ? d.slice(0, 14) + "…" : d,
      score: Math.max(20, Math.min(95, complianceScore + Math.floor(Math.random() * 30 - 15))),
      benchmark: 75,
    }));
  }, [stats.by_process, complianceScore]);

  const heatmapData = useMemo(() => {
    const m = Array.from({ length: 5 }, () =>
      Array.from({ length: 5 }, () => Math.floor(Math.random() * Math.max(1, totalFindings / 5)))
    );
    if (stats.by_severity?.High) m[3][3] += stats.by_severity.High;
    if (stats.by_severity?.Critical) m[4][4] += stats.by_severity.Critical || 0;
    if (stats.by_severity?.Medium) m[2][2] += stats.by_severity.Medium;
    if (stats.by_severity?.Low) m[1][1] += stats.by_severity.Low;
    return m;
  }, [stats, totalFindings]);
  const maxHeat = useMemo(() => Math.max(...heatmapData.flat(), 1), [heatmapData]);

  const trendData = useMemo(() => [
    { period: "Prev−2", findings: Math.floor(totalFindings * 1.4), compliance: Math.max(0, complianceScore - 15) },
    { period: "Prev−1", findings: Math.floor(totalFindings * 1.2), compliance: Math.max(0, complianceScore - 8) },
    { period: "Previous", findings: Math.floor(totalFindings * 1.1), compliance: Math.max(0, complianceScore - 3) },
    { period: "Current", findings: totalFindings, compliance: complianceScore },
  ], [totalFindings, complianceScore]);
  const complianceSpark = useMemo(() => trendData.map((t) => t.compliance), [trendData]);
  const findingsSpark = useMemo(() => trendData.map((t) => t.findings), [trendData]);
  const anomalySpark = useMemo(() => trendData.map((t) => Math.max(0, Math.round(t.findings * 0.35))), [trendData]);
  const riskSpark = useMemo(() => trendData.map((t) => 100 - t.compliance), [trendData]);

  const glossary = useMemo(() => {
    if (report?.glossary && Array.isArray(report.glossary)) return report.glossary;
    if (ctx.glossary && Array.isArray(ctx.glossary)) return ctx.glossary;
    if (report?.glossary && typeof report.glossary === "object")
      return Object.entries(report.glossary).map(([term, definition]) => ({ term, definition: String(definition) }));
    return [];
  }, [report, ctx]);
  const reportApprovalReady = reportSignoff.confirmed && String(reportSignoff.reviewer || "").trim();

  const distributionRoles = useMemo(() => {
    if (ctx.distribution_list && Array.isArray(ctx.distribution_list)) return ctx.distribution_list;
    if (report?.distribution_list && Array.isArray(report.distribution_list)) return report.distribution_list;
    return ["Chief Audit Executive", "Head of Compliance", "Chief Risk Officer", "Senior Management Representative", "Board / Audit Committee Chair"];
  }, [ctx, report]);

  const tocItems = useMemo(() => {
    const items = [];
    let n = 1;
    if (sections.executive_summary) items.push({ num: String(n++), title: "Executive Summary" });
    if (sections.scope) items.push({ num: String(n++), title: "Audit Scope & Objectives" });
    if (sections.methodology) items.push({ num: String(n++), title: "Methodology" });
    if (documentSettings.includeCharts) items.push({ num: String(n++), title: "Executive Dashboard" });
    const fNum = n++;
    if (sections.findings_detail) items.push({ num: String(fNum), title: "Detailed Findings" });
    if (sections.severity_classification) items.push({ num: `${fNum}.1`, title: "Severity Classification" });
    if (sections.risk_exposure) items.push({ num: `${fNum}.2`, title: "Risk Exposure Overview" });
    if (sections.recommendations) items.push({ num: String(n++), title: "Recommendations & Action Plan" });
    if (sections.conclusion) items.push({ num: String(n++), title: "Conclusion" });
    const knownKeys = new Set(["executive_summary", "scope", "methodology", "findings_detail", "severity_classification", "risk_exposure", "recommendations", "conclusion"]);
    Object.keys(sections).filter((k) => !knownKeys.has(k)).forEach((k) => {
      items.push({ num: String(n++), title: k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) });
    });
    if (documentSettings.includeAppendix) {
      items.push({ num: "A", title: "Appendix: Statistics Summary" });
      items.push({ num: "B", title: "Appendix: Severity Definitions" });
      if (glossary.length) items.push({ num: "C", title: "Appendix: Glossary" });
      items.push({ num: glossary.length ? "D" : "C", title: "Appendix: Distribution & Sign-Off" });
    }
    return items;
  }, [sections, documentSettings.includeCharts, documentSettings.includeAppendix, glossary]);

  /* ── shared report document inputs (screen preview, print, HTML export) ── */
  const derived = useMemo(() => ({
    orgName,
    auditType,
    auditName: String(auditRun?.name || ""),
    refId,
    reportDate,
    complianceScore,
    riskScore,
    riskRating,
    totalControls,
    totalFindings,
    findingsLabel,
    severityCounts,
    sevData,
    processData,
    maturityData,
    heatmapData,
    maxHeat,
    trendData,
    glossary,
    distributionRoles,
    tocItems,
    sortedFindings,
    isRelianceRetailDemo,
  }), [
    orgName, auditType, auditRun?.name, refId, reportDate,
    complianceScore, riskScore, riskRating, totalControls, totalFindings,
    findingsLabel, severityCounts, sevData, processData, maturityData,
    heatmapData, maxHeat, trendData, glossary, distributionRoles, tocItems,
    sortedFindings, isRelianceRetailDemo,
  ]);

  /* ── report generation polling ───────────────────────────── */
  /* Polls the persisted run-scoped status (backend-authoritative, survives
     reloads) instead of the Celery result backend. Auto-refreshes the run on
     success and surfaces worker failures via toast. */
  const startReportPolling = useCallback((jobId) => {
    if (reportPollTimer.current) clearTimeout(reportPollTimer.current);
    const pollIntervalMs = 2000;
    const maxAttempts = 180; // ~6 minutes
    let attempts = 0;

    const poll = async () => {
      if (!auditRun?.id) return;
      try {
        const { data: status } = await API.get(`/audit-runs/${auditRun.id}/report/status`);
        const state = status?.state;

        if (state === "success") {
          await refreshRun();
          setGenerating(false);
          toast.success("Report generated", { icon: <Sparkles className="w-4 h-4" /> });
          return;
        }
        if (state === "failed") {
          setGenerating(false);
          toast.error(status?.error || "Report generation failed");
          return;
        }

        attempts += 1;
        if (attempts >= maxAttempts) {
          setGenerating(false);
          toast.error("Report generation timed out. Please try again.");
          return;
        }
        reportPollTimer.current = setTimeout(poll, pollIntervalMs);
      } catch (e) {
        console.error("Failed to poll report status:", e);
        setGenerating(false);
        toast.error("Failed to check report status");
      }
    };

    reportPollTimer.current = setTimeout(poll, pollIntervalMs);
  }, [auditRun?.id, refreshRun]);

  /* Resume polling after a reload/revisit if a generation job is still in flight. */
  useEffect(() => {
    const st = auditRun?.report_generation_status;
    if (auditRun?.id && (st?.state === "queued" || st?.state === "running")) {
      setGenerating(true);
      startReportPolling();
    }
    // eslint-disable-next-line
  }, [auditRun?.id]);

  /* ── handlers ────────────────────────────────────────────── */
  const handleGenerate = async () => {
    if (!reportApprovalReady) {
      toast.error("Reviewer sign-off is required before generating the final report.");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await API.post(`/audit-runs/${auditRun.id}/report`, {
        report_settings: settingsToBackend(documentSettings),
        signoff: signoffToBackend(reportSignoff),
      });

      if (data?.job_id) {
        startReportPolling(data.job_id);
      } else {
        // Backwards-compat: API returned full report synchronously
        setReport(data);
        await refreshRun();
        toast.success("Report generated", { icon: <Sparkles className="w-4 h-4" /> });
        setGenerating(false);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to generate report");
      setGenerating(false);
    }
  };

  const buildPrintJob = useCallback(() => ({
    report,
    context: auditRun?.context || {},
    derived,
    settings: documentSettings,
    signoff: reportSignoff,
  }), [report, auditRun?.context, derived, documentSettings, reportSignoff]);

  /* PDF export = dedicated print root + "Save as PDF" in the print dialog.
     Produces a crisp vector PDF with selectable text (no raster html2canvas). */
  const handleExportPDF = useCallback(() => {
    if (!report) return;
    const firstTime = !localStorage.getItem(PDF_HELP_SEEN_KEY);
    try {
      localStorage.setItem(PDF_HELP_SEEN_KEY, "1");
    } catch {
      // ignore
    }
    requestPrint(buildPrintJob(), { firstTime, toast });
  }, [report, buildPrintJob]);

  /* HTML export reuses the same dedicated render target so the downloaded
     page matches the preview exactly. */
  const handleExportHTML = useCallback(() => {
    if (!report) return;
    exportHtml(buildPrintJob(), { toast });
  }, [report, buildPrintJob]);

  const handleCopyJSON = useCallback(() => {
    if (!auditRun?.id) return;
    navigator.clipboard.writeText(JSON.stringify(report, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("JSON copied");
  }, [auditRun?.id, report]);

  /* ── DOCX export (server-rendered via canonical model) ──── */
  const handleExportDOCX = useCallback(async () => {
    if (!auditRun?.id || !report) return;
    setExporting(true);
    try {
      const response = await API.get(REPORT_DOCX_ENDPOINT(auditRun.id), {
        responseType: "blob",
        params: {
          reviewer: reportSignoff.reviewer || undefined,
          comment: reportSignoff.comment || undefined,
          confirmed: reportSignoff.confirmed || undefined,
        },
      });
      const contentDisposition = response?.headers?.["content-disposition"] || "";
      const match = /filename="([^"]+)"/.exec(contentDisposition);
      const filename = match?.[1] || reportFilename(auditType, "docx");
      downloadBlob(
        new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" }),
        filename,
      );
      toast.success("DOCX exported");
    } catch (e) {
      console.error("DOCX export error:", e);
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to export DOCX");
    } finally {
      setExporting(false);
    }
  }, [auditRun?.id, report, auditType, reportSignoff]);

  /* ── LLM report PDF (AI narrative + verbatim findings) ──── */
  const LLM_POLL_INTERVAL_MS = 2500;
  const LLM_POLL_MAX_ATTEMPTS = 240; // ~10 minutes

  /* loadLlmPdf uses a ref for the run ID so polling closures always hit the
     latest endpoint even if auditRun?.id changes mid-poll. */
  const llmRunIdRef = useRef(auditRun?.id);
  llmRunIdRef.current = auditRun?.id;

  const loadLlmPdf = useCallback(async () => {
    const rid = llmRunIdRef.current;
    if (!rid) return false;
    try {
      const response = await API.get(`/audit-runs/${rid}/report/pdf`, { responseType: "blob" });
      // Validate the blob: a real PDF starts with %PDF (magic bytes 0x25 0x50 0x44 0x46)
      const blob = new Blob([response.data], { type: "application/pdf" });
      if (blob.size < 100) {
        console.error("[LLM PDF] Blob too small, likely an error response:", blob.size, "bytes");
        toast.error("AI report PDF is empty or invalid");
        return false;
      }
      setLlmPdfBlob(blob);
      const url = URL.createObjectURL(blob);
      setLlmPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      return true;
    } catch (e) {
      console.error("Failed to load LLM PDF:", e);
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to load AI report PDF");
      return false;
    }
  }, [toast]); // no auditRun?.id dep — uses ref

  const downloadLlmPdf = useCallback(() => {
    if (!llmPdfBlob) {
      toast.error("AI report PDF not loaded yet");
      return;
    }
    downloadBlob(llmPdfBlob, reportFilename(auditType, "pdf"));
    toast.success("AI report PDF downloaded");
  }, [llmPdfBlob, auditType, toast]);

  /* pollLlmStatus: polls until terminal state or max attempts.
     Uses a ref for the load function so closures never go stale. */
  const loadLlmPdfRef = useRef(loadLlmPdf);
  loadLlmPdfRef.current = loadLlmPdf;

  const pollLlmStatus = useCallback(() => {
    if (llmPollTimer.current) clearTimeout(llmPollTimer.current);
    let attempts = 0;
    const poll = async () => {
      const rid = llmRunIdRef.current;
      if (!rid) return;
      try {
        const { data } = await API.get(`/audit-runs/${rid}/report/llm-status`);
        const state = data?.state;
        setLlmPdfStatus(data || { state: "none" });
        if (state === "success") {
          // Retry PDF load up to 3 times (worker may still be writing bytes)
          let loaded = false;
          for (let retry = 0; retry < 3 && !loaded; retry++) {
            if (retry > 0) await new Promise((r) => setTimeout(r, 1000));
            loaded = await loadLlmPdfRef.current();
          }
          if (!loaded) {
            toast.error("AI report generated but PDF failed to load. Try refreshing.");
          } else {
            toast.success(`AI report PDF ready (${data.findings_count ?? "?"} findings)`);
          }
          return;
        }
        if (state === "failed") {
          toast.error(data?.error || "AI report generation failed");
          return;
        }
        if (state === "queued" || state === "running") {
          attempts += 1;
          if (attempts >= LLM_POLL_MAX_ATTEMPTS) {
            toast.error("AI report generation timed out. Please try again.");
            return;
          }
          llmPollTimer.current = setTimeout(poll, LLM_POLL_INTERVAL_MS);
          return;
        }
        // state === "none" or unknown — stop polling
      } catch (e) {
        console.error("Failed to poll LLM report status:", e);
        attempts += 1;
        if (attempts >= LLM_POLL_MAX_ATTEMPTS) {
          toast.error("AI report polling failed repeatedly. Please refresh and try again.");
          return;
        }
        llmPollTimer.current = setTimeout(poll, LLM_POLL_INTERVAL_MS);
      }
    };
    llmPollTimer.current = setTimeout(poll, 1500);
  }, [toast]); // no auditRun?.id or loadLlmPdf dep — uses refs

  const handleExportLLMPDF = useCallback(async () => {
    if (!auditRun?.id) return;
    setExporting(true);
    try {
      const { data } = await API.post(`/audit-runs/${auditRun.id}/report/llm-pdf`);
      setLlmPdfStatus({ state: "queued", job_id: data?.job_id });
      toast.info("AI report generation started — narrative drafted by the model, findings kept verbatim.");
      pollLlmStatus();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to start AI report");
    } finally {
      setExporting(false);
    }
  }, [auditRun?.id, pollLlmStatus]);

  /* Seed from persisted status on load / after report refresh. */
  useEffect(() => {
    const st = auditRun?.llm_report_status;
    if (st?.state === "success") {
      setLlmPdfStatus(st);
      loadLlmPdf();
    } else if (st?.state === "queued" || st?.state === "running") {
      setLlmPdfStatus(st);
      pollLlmStatus();
    } else if (st?.state === "failed") {
      // Show persisted failure but don't re-poll — user must retry manually
      setLlmPdfStatus(st);
    }
    // eslint-disable-next-line
  }, [auditRun?.id]);

  /* Without a classic report only the AI report export is available. */
  useEffect(() => {
    if (!report && exportFormat !== "llmpdf") setExportFormat("llmpdf");
  }, [report, exportFormat]);

  /* ── renderers ───────────────────────────────────────────── */

  /* FIX: Finding cards use INLINE STYLES for borders/backgrounds
     so they survive the printable document capture. */
  const renderFindingCard = (item, idx) => {
    if (typeof item === "string") return <p key={idx} className="mb-2 text-sm">{item}</p>;
    if (typeof item !== "object") return <p key={idx} className="mb-2 text-sm">{String(item)}</p>;

    const TECH_PREFIXES = [
      /^SQL Policy Validation:\s*/i, /^SQL Validation:\s*/i,
      /^Policy Validation:\s*/i, /^Policy Check:\s*/i,
      /^Control Check:\s*/i, /^RAG Validation:\s*/i,
      /^RAG Check:\s*/i, /^Control Validation:\s*/i,
      /^Audit Control:\s*/i, /^Rule Check:\s*/i,
    ];
    const stripTech = (t) => {
      let s = String(t || "");
      for (const re of TECH_PREFIXES) s = s.replace(re, "");
      return s.replace(/_/g, " ").replace(/^[:\-–\s]+/, "").trim() || t;
    };

    const controlRef = item.control_ref || item.finding_id || item.control_id || item.id || `Finding ${idx + 1}`;
    const rawDescription = item.description || item.finding_description || item.finding_text || item.title || "";
    const description = stripTech(rawDescription);
    const evidenceNarrative = item.evidence_narrative || item.summary || "";
    const verdict = item.verdict || "INCONCLUSIVE";
    
    // Attempt to extract where_to_look as an array
    const rawWhere = item.where_to_look;
    const whereToLook = Array.isArray(rawWhere) ? rawWhere : (typeof rawWhere === "string" ? [rawWhere] : []);

    const severity = verdict === "FAIL" ? "High" : verdict === "INCONCLUSIVE" ? "Observation" : "Low";
    
    const cardTextColor = isDarkMode ? "#e5eefb" : "#1f2937";
    const cardMutedTextColor = isDarkMode ? "#a8b6cf" : "#4b5563";
    const cardIconColor = isDarkMode ? "#7f8ea8" : "#9ca3af";
    const cardBorderColor = isDarkMode ? "#334155" : "#e5e7eb";
    const cardBgColor = isDarkMode
      ? (() => {
          const map = { Critical: "#261316", High: "#261316", Medium: "#2a2214", Low: "#162235", Observation: "#111827" };
          return map[severity] || "#111827";
        })()
      : sevBgColor(severity);

    return (
      <div
        key={idx}
        className="no-break phase10-finding-card"
        style={{
          marginBottom: "16px",
          padding: "20px",
          border: `1px solid ${cardBorderColor}`,
          borderLeft: `4px solid ${sevBorderColor(severity)}`,
          borderRadius: "8px",
          backgroundColor: cardBgColor,
          pageBreakInside: "avoid",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: "12px" }}>
          <div style={{ flexShrink: 0, marginTop: "2px" }}>
            <SeverityIcon severity={severity} />
          </div>
          <div style={{ flex: 1 }}>
             <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontWeight: 800, fontSize: "12px", color: sevBorderColor(severity), fontFamily: "monospace", letterSpacing: "0.5px" }}>{controlRef}</span>
                  <span
                    style={{
                      fontSize: "10px",
                      padding: "2px 8px",
                      borderRadius: "9999px",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      backgroundColor: verdict === "FAIL" ? "#fef2f2" : "#f8fafc",
                      color: verdict === "FAIL" ? "#ef4444" : "#64748b",
                      border: `1px solid ${verdict === "FAIL" ? "#fecaca" : "#cbd5e1"}`,
                    }}
                  >
                    {verdict}
                  </span>
                </div>
             </div>
             <p style={{ fontSize: "14px", fontWeight: 700, color: cardTextColor, margin: "0 0 8px 0", lineHeight: 1.4 }}>{description}</p>
          </div>
        </div>

        {evidenceNarrative && (
          <div style={{ marginLeft: "28px", marginBottom: "12px", padding: "12px", backgroundColor: isDarkMode ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)", borderRadius: "6px", border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}` }}>
            <p style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", color: cardIconColor, marginBottom: "4px" }}>Evidence Narrative</p>
            <p style={{ fontSize: "12px", color: cardTextColor, lineHeight: 1.6 }}>{evidenceNarrative}</p>
          </div>
        )}

        {whereToLook.length > 0 && (
          <div style={{ marginLeft: "28px", display: "flex", flexDirection: "column", gap: "4px" }}>
            <p style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", color: cardIconColor, marginBottom: "2px" }}>Where to Look</p>
            <ul style={{ paddingLeft: "16px", margin: 0, fontSize: "11px", color: cardMutedTextColor }}>
              {whereToLook.map((w, wIdx) => (
                <li key={wIdx} style={{ marginBottom: "2px", lineHeight: 1.4 }}>{String(w)}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderContent = (content) => {
    if (!content) return null;
    if (typeof content === "string") return <p style={{ fontSize: "13px", lineHeight: 1.7, whiteSpace: "pre-wrap", color: "#374151" }}>{content}</p>;
    if (Array.isArray(content)) {
      if (
        content.length > 0
        && typeof content[0] === "object"
        && (content[0].priority || content[0].action || content[0].timeline)
      ) {
        return (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
            <thead>
              <tr style={{ background: "#f9fafb" }}>
                {["Priority", "Action", "Owner", "Timeline", "Success Metric"].map((h) => (
                  <th key={h} style={{ padding: "8px 10px", textAlign: "left", borderBottom: "2px solid #e5e7eb", fontWeight: 700, color: "#6b7280", fontSize: "11px" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {content.map((item, idx) => (
                <tr key={idx} className="table-row-anim" style={{ borderBottom: "1px solid #f3f4f6", "--row-delay": `${idx * 55}ms` }}>
                  <td style={{ padding: "8px 10px", fontWeight: 700, color: "#1f2937", whiteSpace: "nowrap" }}>{item.priority || "N/A"}</td>
                  <td style={{ padding: "8px 10px", color: "#374151", lineHeight: 1.5 }}>{item.action || item.recommendation || "N/A"}</td>
                  <td style={{ padding: "8px 10px", color: "#374151" }}>{item.owner || "N/A"}</td>
                  <td style={{ padding: "8px 10px", color: "#374151", whiteSpace: "nowrap" }}>{item.timeline || "N/A"}</td>
                  <td style={{ padding: "8px 10px", color: "#374151", lineHeight: 1.5 }}>{item.success_metric || item.rationale || "N/A"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      }
      if (content.length > 0 && typeof content[0] === "object" && (content[0].finding_id || content[0].severity || content[0].description))
        return <div>{content.map((item, idx) => renderFindingCard(item, idx))}</div>;
      return (
        <ul style={{ paddingLeft: "20px", margin: 0 }}>
          {content.map((item, idx) => (
            <li key={idx} style={{ fontSize: "13px", color: "#374151", marginBottom: "4px", lineHeight: 1.6 }}>
              {typeof item === "object" ? item.recommendation || item.action || item.description || item.text || JSON.stringify(item) : String(item)}
            </li>
          ))}
        </ul>
      );
    }
    if (typeof content === "object") {
      if (content.findings && Array.isArray(content.findings))
        return <div>{content.findings.map((i, idx) => renderFindingCard(i, idx))}</div>;
      return (
        <dl style={{ margin: 0 }}>
          {Object.entries(content).map(([key, val]) => (
            <div key={key} style={{ marginBottom: "8px" }}>
              <dt style={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>{key.replace(/_/g, " ")}</dt>
              <dd style={{ fontSize: "13px", color: "#374151", margin: "2px 0 0" }}>{typeof val === "string" ? val : JSON.stringify(val)}</dd>
            </div>
          ))}
        </dl>
      );
    }
    return <p style={{ fontSize: "13px", color: "#374151" }}>{String(content)}</p>;
  };

  /* ════════════════════════════════════════════════════════════
     RENDER
     ════════════════════════════════════════════════════════════ */
  return (
    <PageLayout className="space-y-6 sm:space-y-8 max-w-7xl mx-auto pb-12" data-testid="phase-10">
      {/* header */}
      <div className={`phase10-report-header metric-card-anim flex items-start justify-between flex-wrap gap-4 ${report && !generating ? "phase10-report-header-attached" : ""}`} style={{ "--delay": "20ms" }}>
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-500/25">
            <FileText className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-950 dark:text-white">Phase 10: Final Audit Report</h2>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-emerald-100 mt-0.5">
              {isRelianceRetailDemo
                ? "Generate the Reliance Retail close-out report with the shrinkage-forensics narrative front and center."
                : "Generate, customise, and export the comprehensive audit report."}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {report?.audit_mode === "team" && (
            <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/30 h-9 px-3">
              <Users className="w-3 h-3 mr-1" /> Team Mode
            </Badge>
          )}
          {report && (
            <>
              <Button variant="ghost" size="icon" onClick={handleCopyJSON} title="Copy JSON" className="rounded-xl h-9 w-9">
                {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={handleExportPDF} title="Print / Save as PDF" className="rounded-xl h-9 w-9">
                <Printer className="w-4 h-4" />
              </Button>
            </>
          )}
          <Select value={exportFormat} onValueChange={(v) => setExportFormat(v)}>
            <SelectTrigger className="w-[150px] h-9 rounded-xl text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-xl">
              {report && <SelectItem value="pdf">PDF (Save as PDF)</SelectItem>}
              <SelectItem value="llmpdf">PDF (AI Report)</SelectItem>
              {report && <SelectItem value="html">HTML</SelectItem>}
              {report && <SelectItem value="docx">Word (DOCX)</SelectItem>}
            </SelectContent>
          </Select>
              <Button variant="outline" onClick={() => {
            if (exportFormat === "pdf") handleExportPDF();
            else if (exportFormat === "llmpdf") handleExportLLMPDF();
            else if (exportFormat === "docx") handleExportDOCX();
            else handleExportHTML();
          }} disabled={exporting || !reportApprovalReady} className="gap-2 rounded-xl text-xs h-9 px-4 font-semibold">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {exporting ? "Exporting…" : "Export"}
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={generating || !auditRun}
            data-testid="generate-report-btn"
            className="gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-10 px-5 shadow-lg shadow-emerald-600/30 transition-all active:scale-95 border-0"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}

            {generating ? "Generating…" : report ? "Regenerate Report" : "Generate Report"}
          </Button>
        </div>
      </div>

      {isRelianceRetailDemo && (
        <Card className="border-orange-200/80 bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 shadow-sm dark:border-orange-300/20 dark:from-amber-400/10 dark:via-slate-900 dark:to-rose-400/10">
          <CardContent className="py-5 space-y-4">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-orange-600 dark:text-orange-300" />
              <p className="text-sm font-semibold">Reliance Retail report focus</p>
            </div>
            <p className="text-sm leading-6 text-slate-700 dark:text-slate-100">
              Frame the report around shrinkage forensics at <span className="font-mono">SBL-MP-014</span>, with supporting sections on supplier fraud,
              refund abuse, complaint intelligence, and contract leakage. The close should make the consensus point clearly: multiple evidence streams
              converged on one attributable shrinkage answer.
            </p>
            <div className="flex flex-wrap gap-2">
              {RELIANCE_REPORT_SPINE.map((item) => (
                <Badge key={item} variant="outline" className="text-xs">
                  {item}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-blue-200/70 bg-white/80 dark:border-cyan-300/20 dark:bg-slate-950/60">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <UserCheck className="h-4 w-4 text-blue-500 dark:text-cyan-300" />
            Final Reviewer Sign-off
          </CardTitle>
          <CardDescription>
            Confirm reviewer approval before generating or exporting the final report.
            {reportSignoff.confirmed && (
              <span className="block mt-1 text-emerald-600 dark:text-emerald-300">
                Approved{reportSignoff.reviewer ? ` by ${reportSignoff.reviewer}` : ""}
                {reportSignoff.confirmed_at ? ` on ${new Date(reportSignoff.confirmed_at).toLocaleString()}` : ""}
                {reportSignoff.report_version ? ` — report v${reportSignoff.report_version}` : ""}
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Reviewer Name</Label>
            <Input
              value={reportSignoff.reviewer}
              onChange={(e) => updateSignoff({ reviewer: e.target.value })}
              placeholder="Enter auditor or manager name"
            />
          </div>
          <div className="space-y-2">
            <Label>Approval Status</Label>
            <div className="flex h-10 items-center justify-between rounded-xl border border-border/70 bg-background/70 px-3">
              <span className="text-sm text-muted-foreground">I confirm the report is ready for issuance</span>
              <Switch
                checked={!!reportSignoff.confirmed}
                onCheckedChange={(checked) => updateSignoff({ confirmed: checked })}
              />
            </div>
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Management Sign-off Note</Label>
            <Textarea
              rows={3}
              value={reportSignoff.comment}
              onChange={(e) => updateSignoff({ comment: e.target.value })}
              placeholder="Optional formal note to include in the final report sign-off section"
            />
            <p className="text-xs text-muted-foreground">
              Sign-off details are persisted to the backend (autosaved) and appear in the report, Print/PDF, HTML and Word exports.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* empty */}
      {!report && !generating && (
        <Card className="border-dashed border-2">
          <CardContent className="py-16 text-center">
            <div className="relative inline-block mb-4">
              <div className="absolute inset-0 bg-emerald-400/20 rounded-full blur-xl animate-pulse" />
              <div className="relative rounded-full border border-emerald-200 bg-gradient-to-br from-emerald-500/10 to-teal-700/10 p-4 dark:border-emerald-400/20">
                <FileOutput className="w-10 h-10 text-emerald-500" />
              </div>
            </div>
            <h3 className="text-lg font-semibold mb-1">Report Ready for Generation</h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              All audit phases are complete. Click <strong>Generate Report</strong> to compile findings, compliance analytics, control test results, and recommendations.
            </p>
            <div className="flex gap-4 justify-center mt-6 text-xs text-muted-foreground">
              {["Controls tested", "Evidence reviewed", "Findings classified"].map((t) => (
                <div key={t} className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> {t}</div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* generating */}
      {generating && (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="relative inline-block mb-4">
              <Loader2 className="w-12 h-12 text-emerald-500 animate-spin" />
              <Sparkles className="w-5 h-5 text-emerald-300 absolute -top-1 -right-1 animate-pulse" />
            </div>
            <h3 className="text-lg font-semibold mb-1">Generating Audit Report…</h3>
            <p className="text-sm text-muted-foreground">Compiling findings, control test results, and compliance metrics into the final report.</p>
            <div className="max-w-xs mx-auto mt-4"><Progress value={65} className="h-1.5" /></div>
          </CardContent>
        </Card>
      )}

      {report && !generating && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {[
              { label: "Controls", value: animControls, icon: Shield, color: "text-emerald-500", bg: "bg-emerald-500/10", spark: findingsSpark, sparkColor: "#10b981" },
              { label: "Anomalies", value: animAnomalies, icon: AlertTriangle, color: "text-emerald-500", bg: "bg-emerald-500/10", spark: anomalySpark, sparkColor: "#34d399" },
              { label: "Compliance", value: `${animCompliance}%`, icon: CheckCircle2, color: "text-blue-500", bg: "bg-blue-500/10", spark: complianceSpark, sparkColor: "#22c55e" },
              { label: "Risk Score", value: `${riskScore}%`, icon: Activity, color: "text-blue-500", bg: "bg-blue-500/10", spark: riskSpark, sparkColor: "#2563eb" },
              { label: "High/Crit", value: (severityCounts.High || 0) + (severityCounts.Critical || 0), icon: TriangleAlert, color: "text-emerald-600", bg: "bg-emerald-600/10", spark: anomalySpark, sparkColor: "#34d399" },
              { label: "Processes", value: processData.length, icon: Layers, color: "text-blue-500", bg: "bg-blue-500/10", spark: findingsSpark, sparkColor: "#3b82f6" },
            ].map((kpi, i) => (
              <Card key={i} className="metric-card-anim executive-glass overflow-hidden border-none shadow-sm bg-card/50 backdrop-blur" style={{ "--delay": `${i * 70}ms` }}>
                <CardContent className="py-3 px-4 flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${kpi.bg}`}><kpi.icon className={`w-4 h-4 ${kpi.color}`} /></div>
                  <div>
                    <p className="text-2xl font-bold font-mono leading-none">{kpi.value}</p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">{kpi.label}</p>
                    <MiniSparkline values={kpi.spark} color={kpi.sparkColor} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* gauge / radar / trend + quick actions */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <Card className="metric-card-anim executive-glass xl:col-span-1" style={{ "--delay": "120ms" }}>
              <CardHeader className="pb-0"><CardTitle className="text-sm flex items-center gap-2"><Gauge className="w-4 h-4" /> Compliance Score</CardTitle></CardHeader>
              <CardContent className="flex items-center justify-center py-4">
                <RiskGauge score={complianceScore || 78} label="Overall Compliance" size={160} />
              </CardContent>
            </Card>
            <Card className="metric-card-anim executive-glass xl:col-span-1" style={{ "--delay": "180ms" }}>
              <CardHeader className="pb-0"><CardTitle className="text-sm flex items-center gap-2"><Target className="w-4 h-4" /> Maturity Radar</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={180}>
                  <RadarChart data={maturityData}>
                    <PolarGrid stroke="#e5e7eb" />
                    <PolarAngleAxis dataKey="domain" tick={{ fontSize: 9 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 8 }} />
                    <Radar name="Score" dataKey="score" stroke="#2563eb" fill="#2563eb" fillOpacity={0.3} />
                    <Radar name="Benchmark" dataKey="benchmark" stroke="#94a3b8" fill="none" strokeDasharray="4 4" />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card className="metric-card-anim executive-glass xl:col-span-1" style={{ "--delay": "240ms" }}>
              <CardHeader className="pb-0"><CardTitle className="text-sm flex items-center gap-2"><TriangleAlert className="w-4 h-4" /> Risk Score</CardTitle></CardHeader>
              <CardContent className="flex items-center justify-center py-4">
                <RiskGauge score={riskScore || 25} label="Residual Risk" size={160} />
              </CardContent>
            </Card>
          </div>

          {/* charts row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sevData.length > 0 && (
              <Card className="metric-card-anim executive-glass" style={{ "--delay": "300ms" }}>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><PieChartIcon className="w-4 h-4" /> Severity Distribution</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart><Pie data={sevData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name}: ${value}`} labelLine={false} isAnimationActive animationDuration={1400} animationBegin={150}>{sevData.map((e, i) => <Cell key={i} fill={e.fill} />)}</Pie><Tooltip /></PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
            {processData.length > 0 && (
              <Card className="metric-card-anim executive-glass" style={{ "--delay": "360ms" }}>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4" /> By Process Area</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={processData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis type="number" tick={{ fontSize: 9 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} width={100} />
                      <Tooltip /><Bar dataKey="value" fill="#2563eb" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
            <Card className="metric-card-anim executive-glass" style={{ "--delay": "420ms" }}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><Activity className="w-4 h-4" /> Risk Heatmap</CardTitle>
                <CardDescription className="text-[10px]">Impact × Likelihood</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-1 heatmap-threshold-bg rounded-lg p-2">
                  {["Very High", "High", "Medium", "Low", "Very Low"].map((lk, li) => (
                    <div key={li} className="flex items-center gap-1">
                      <span className="text-[9px] w-14 text-right text-muted-foreground truncate">{lk}</span>
                      {[0, 1, 2, 3, 4].map((ii) => <HeatmapCell key={ii} value={heatmapData[4 - li]?.[ii] || 0} max={maxHeat} label={`${lk} × Impact ${ii + 1}`} />)}
                    </div>
                  ))}
                  <div className="flex items-center gap-1 mt-1">
                    <span className="w-14" />
                    {["V.Low", "Low", "Med", "High", "V.High"].map((l) => <span key={l} className="text-[8px] text-center text-muted-foreground flex-1">{l}</span>)}
                  </div>
                  <p className="text-[9px] text-center text-muted-foreground mt-1">← Impact →</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* document settings */}
          <Card>
            <CardHeader className="pb-2">
              <button type="button" onClick={() => setSettingsOpen((o) => !o)} className="w-full flex items-center justify-between gap-2 text-left">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Settings2 className="w-4 h-4" /> Document Settings
                </CardTitle>
                <span className="text-xs text-muted-foreground">{settingsOpen ? "Collapse" : "Expand"}</span>
              </button>
              <CardDescription className="text-xs">
                Applied to the report preview, Print/PDF and Word export. Persisted with the report when you generate.
              </CardDescription>
            </CardHeader>
            {settingsOpen && (
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Theme</Label>
                    <Select value={documentSettings.theme} onValueChange={(v) => updateSetting("theme", v)}>
                      <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="minimal">Minimal</SelectItem>
                        <SelectItem value="modern">Modern</SelectItem>
                        <SelectItem value="classic">Classic</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Page Size</Label>
                    <Select value={documentSettings.pageSize} onValueChange={(v) => updateSetting("pageSize", v)}>
                      <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A4">A4</SelectItem>
                        <SelectItem value="Letter">Letter</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Orientation</Label>
                    <Select value={documentSettings.orientation} onValueChange={(v) => updateSetting("orientation", v)}>
                      <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="portrait">Portrait</SelectItem>
                        <SelectItem value="landscape">Landscape</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  </div>
                <div className="flex flex-wrap gap-x-6 gap-y-3">
                  {[
                    ["cover", "Cover Page", "includeCover"],
                    ["toc", "Table of Contents", "includeToc"],
                    ["charts", "Dashboard Charts", "includeCharts"],
                    ["appendix", "Appendices", "includeAppendix"],
                    ["wm", "Watermark", "showWatermark"],
                    ["conf", "Confidential Banner", "showConfidential"],
                  ].map(([id, label, key]) => (
                    <div key={id} className="flex items-center gap-2">
                      <Switch id={`ds-${id}`} checked={!!documentSettings[key]} onCheckedChange={(v) => updateSetting(key, v)} />
                      <Label htmlFor={`ds-${id}`} className="text-xs cursor-pointer">{label}</Label>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>

          {/* tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="flex-wrap h-auto gap-1">
              <TabsTrigger value="overview" className="text-xs gap-1"><Eye className="w-3 h-3" /> Overview</TabsTrigger>
              <TabsTrigger value="findings" className="text-xs gap-1"><FileWarning className="w-3 h-3" /> Findings</TabsTrigger>
              <TabsTrigger value="recommendations" className="text-xs gap-1"><CheckCircle2 className="w-3 h-3" /> Recommendations</TabsTrigger>
              <TabsTrigger value="fullreport" className="text-xs gap-1"><BookOpen className="w-3 h-3" /> Full Report</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4 mt-4">
              {sections.executive_summary && <Card><CardHeader><CardTitle className="text-base">Executive Summary</CardTitle></CardHeader><CardContent>{renderContent(sections.executive_summary)}</CardContent></Card>}
              {sections.scope && <Card><CardHeader><CardTitle className="text-base">Scope</CardTitle></CardHeader><CardContent>{renderContent(sections.scope)}</CardContent></Card>}
              {sections.methodology && <Card><CardHeader><CardTitle className="text-base">Methodology</CardTitle></CardHeader><CardContent>{renderContent(sections.methodology)}</CardContent></Card>}
            </TabsContent>

            <TabsContent value="findings" className="space-y-4 mt-4">
              <Card>
                <CardHeader><div className="flex items-center justify-between"><CardTitle className="text-base">Detailed Findings</CardTitle><Badge variant="secondary">{totalFindings} {findingsLabel === "Finding Themes" ? "themes" : "findings"}</Badge></div></CardHeader>
                <CardContent>
                  {sortedFindings.length > 0 ? renderContent(sortedFindings) : renderContent(sections.findings_detail)}
                  {sections.severity_classification && <div className="mt-6"><h4 className="text-sm font-semibold mb-2">Severity Classification</h4>{renderContent(sections.severity_classification)}</div>}
                </CardContent>
              </Card>
              {sections.raw_findings_appendix && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Raw Findings Appendix</CardTitle>
                      <Badge variant="outline">
                        {Array.isArray(sections.raw_findings_appendix) ? sections.raw_findings_appendix.length : 0} findings
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {renderContent(sections.raw_findings_appendix)}
                  </CardContent>
                </Card>
              )}
              {sections.risk_exposure && <Card><CardHeader><CardTitle className="text-base">Risk Exposure Overview</CardTitle></CardHeader><CardContent>{renderContent(sections.risk_exposure)}</CardContent></Card>}
            </TabsContent>

            <TabsContent value="recommendations" className="space-y-4 mt-4">
              {sections.recommendations && <Card><CardHeader><CardTitle className="text-base">Recommendations & Action Plan</CardTitle></CardHeader><CardContent>{renderContent(sections.recommendations)}</CardContent></Card>}
              {sections.conclusion && <Card><CardHeader><CardTitle className="text-base">Conclusion</CardTitle></CardHeader><CardContent>{renderContent(sections.conclusion)}</CardContent></Card>}
            </TabsContent>

            <TabsContent value="fullreport" className="mt-4">
              {llmPdfUrl ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                      <Sparkles className="w-3.5 h-3.5 text-emerald-500" />
                      AI report —  Ai-written narrative with findings/metrics kept verbatim
                      {llmPdfStatus?.findings_count ? ` · ${llmPdfStatus.findings_count} findings` : ""}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button variant="outline" size="sm" className="h-8 rounded-xl text-xs" onClick={downloadLlmPdf}>
                        <Download className="w-3.5 h-3.5 mr-1" /> Download
                      </Button>
                      <Button variant="outline" size="sm" className="h-8 rounded-xl text-xs" onClick={() => setLlmPdfUrl(null)}>
                        Show classic preview
                      </Button>
                    </div>
                  </div>
                  <div className="rounded-xl border border-border/70 overflow-hidden bg-white" style={{ height: "80vh" }}>
                    <iframe src={llmPdfUrl} title="AI Report PDF" className="w-full h-full" />
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-xs text-muted-foreground">
                      React-rendered document preview (used for Print/Save-as-PDF and HTML export).
                    </div>
                    {auditRun?.llm_report_status?.state === "success" && (
                      <Button variant="outline" size="sm" className="h-8 rounded-xl text-xs" onClick={loadLlmPdf}>
                        <Sparkles className="w-3.5 h-3.5 mr-1 text-emerald-500" /> Show AI report
                      </Button>
                    )}
                  </div>
                  <div className="rd-page-sheet" style={{ maxWidth: 900 }}>
                    <ReportDocument
                      report={report}
                      context={ctx}
                      derived={derived}
                      settings={documentSettings}
                      signoff={docSignoff}
                    />
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* stats card */}
          <Card className="bg-gradient-to-r from-slate-50 to-slate-100/50 dark:from-slate-900 dark:to-slate-800/50">
            <CardContent className="py-5">
              <div className="flex gap-8 text-center flex-wrap justify-center">
                {[
                  { value: totalControls, label: "Controls", color: "text-red-600" },
                  { value: totalFindings, label: "Findings", color: "text-red-600" },
                  { value: `${complianceScore}%`, label: "Compliance", color: "text-red-600" },
                  ...(stats.team_findings ? [{ value: stats.team_findings, label: "Team Validated", color: "text-red-600" }] : []),
                  ...Object.entries(stats.by_severity || {}).map(([sev, count]) => ({
                    value: count, label: sev,
                    color: sev === "Critical" || sev === "High" ? "text-red-600" : "text-slate-600",
                  })),
                ].map((item, i) => (
                  <div key={i} className="min-w-[80px]">
                    <p className={`text-2xl font-bold font-mono leading-none ${item.color}`}>{item.value}</p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1 font-medium">{item.label}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </PageLayout>
  );
}
